import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { PromoteMediaDraft } from '../draftStore';
import { composerMediaMeta, composerMediaTitle } from './composerMediaText';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerAttachedMediaProps {
  media?: PromoteMediaDraft;
  /** The song of the attached file when the post links to another release. */
  mediaSong?: { uuid: string; title: string };
  /** The title of the release that the post links to. */
  releaseTitle?: string;
  onLinkToMediaSong?: () => void;
  onOpenPicker: () => void;
  onDetach: () => void;
}

/** The image or the video riding along with the post. */
const ComposerAttachedMediaComponent: React.FC<ComposerAttachedMediaProps> = ({
  media,
  mediaSong,
  releaseTitle,
  onLinkToMediaSong,
  onOpenPicker,
  onDetach,
}) => {
  const intl = useIntl();

  return (
    <div className={styles.stack}>
      {media === undefined ? (
        <div className={styles.fieldRow}>
          <SMLButton onClick={onOpenPicker}>
            {intl.formatMessage({ id: 'app.sml.promote.composer.media.attachMedia', defaultMessage: 'Attach media' })}
          </SMLButton>
          <span className={styles.statNote}>
            {intl.formatMessage({
              id: 'app.sml.promote.composer.media.none',
              defaultMessage: 'Facebook, Threads and X take text only. Instagram, TikTok and YouTube need a clip.',
            })}
          </span>
        </div>
      ) : (
        <div className={styles.mediaRow} data-testid="composer-attached-media">
          {media.thumbnailUrl !== undefined ? (
            <img className={styles.releaseArt} src={media.thumbnailUrl} alt="" />
          ) : null}
          <div className={styles.rowBody}>
            <div className={styles.rowTitle}>{composerMediaTitle(intl, media)}</div>
            <div className={styles.monoMeta}>{composerMediaMeta(intl, media)}</div>
          </div>
          <div className={styles.rowRight}>
            <SMLButton onClick={onOpenPicker}>
              {intl.formatMessage({ id: 'app.sml.promote.composer.media.change', defaultMessage: 'Change' })}
            </SMLButton>
            <SMLButton onClick={onDetach}>
              {intl.formatMessage({ id: 'app.sml.promote.composer.media.remove', defaultMessage: 'Remove' })}
            </SMLButton>
          </div>
        </div>
      )}

      {media !== undefined && mediaSong !== undefined ? (
        <div className={styles.fieldRow} data-testid="composer-media-other-song">
          <span className={styles.statNote}>
            {intl.formatMessage(
              {
                id: 'app.sml.promote.composer.media.otherSong',
                defaultMessage: 'This file is from {song}. The post links to {release}.',
              },
              { song: mediaSong.title, release: releaseTitle ?? '' }
            )}
          </span>
          {onLinkToMediaSong !== undefined ? (
            <SMLButton onClick={onLinkToMediaSong}>
              {intl.formatMessage(
                { id: 'app.sml.promote.composer.media.linkToSong', defaultMessage: 'Link to {song} instead' },
                { song: mediaSong.title }
              )}
            </SMLButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export const ComposerAttachedMedia = TypedMemo(ComposerAttachedMediaComponent);
